import { Button } from "@/components/ui/button/button";
import { Step, Stepper } from "@/components/ui/stepper/stepper";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateNC } from "../../queries/nc/use-create-nc";
import { AddDetails, useDetailsForm } from "./steps/add-details";
import { SelectFailures, useFailuresForm } from "./steps/select-failures";
import { SelectMTU, useMTUForm } from "./steps/select-mtu";
import { ErrorPopover } from "@/components/ui/error-popover/error-popover";

export function CreateNC({
  onSuccess,
  onCancel,
}: {
  onSuccess?: (id?: string) => void;
  onCancel?: VoidFunction;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const mtuForm = useMTUForm();
  const failuresForm = useFailuresForm();
  const detailsForm = useDetailsForm();
  const { createNC, isPending, error } = useCreateNC({
    onSuccess: (res) => onSuccess?.(res.NonConformanceNId ?? undefined),
  });

  const advanceStep = useCallback((bool: boolean) => {
    if (bool) setStep((x) => x + 1);
  }, []);

  const tryAdvanceMTU = useCallback(async () => {
    advanceStep(await mtuForm.trigger());
  }, [advanceStep, mtuForm]);

  const tryAdvanceFailures = useCallback(async () => {
    advanceStep(await failuresForm.trigger());
  }, [advanceStep, failuresForm]);

  const trySubmit = useCallback(async () => {
    const valid = await detailsForm.trigger();

    if (!valid) return;

    createNC({
      failureNIds: failuresForm.getValues("failures"),
      mtuNIds: mtuForm.getValues("mtuCodes"),
      notes: detailsForm.getValues("note"),
      severity: detailsForm.getValues("severity"),
      documentsIds: detailsForm.getValues("documents"),
    });
  }, [createNC, detailsForm, failuresForm, mtuForm]);

  const handleBack = useCallback(async () => {
    setStep((x) => Math.max(x - 1, 0));
  }, []);

  return (
    <>
      <div className="min-h-96 flex flex-col h-full bg-white">
        <Stepper desiredStepIndex={step}>
          <Step
            title={t("nc.failures.title")}
            valid={failuresForm.formState.isValid}
          >
            <div className="p-5 overflow-auto h-full">
              <SelectFailures form={failuresForm} />
            </div>
            <Actions onCancel={onCancel} onNext={tryAdvanceFailures} />
          </Step>
          <Step title={t("nc.mtu.title")} valid={mtuForm.formState.isValid}>
            <div className="p-5 overflow-auto h-full">
              <SelectMTU form={mtuForm} />
            </div>
            <Actions
              onBack={handleBack}
              onCancel={onCancel}
              onNext={tryAdvanceMTU}
            />
          </Step>
          <Step
            title={t("nc.details.title")}
            valid={detailsForm.formState.isValid}
          >
            <div className="p-5 overflow-auto h-full">
              <AddDetails
                form={detailsForm}
                failureNIds={failuresForm.getValues("failures")}
                mtuCodes={mtuForm.getValues("mtuCodes")}
              />
            </div>
            <Actions
              onBack={handleBack}
              onCancel={onCancel}
              onNext={trySubmit}
              loading={isPending}
              submitContent={
                <>
                  <Check className="w-4 h-4 " />
                  {t("nc.submit")}
                </>
              }
              error={error}
            />
          </Step>
        </Stepper>
      </div>
    </>
  );
}

function Actions({
  onBack,
  onNext,
  onCancel,
  loading,
  submitContent,
  error,
}: {
  onBack?: VoidFunction;
  onNext: VoidFunction;
  onCancel?: VoidFunction;
  loading?: boolean;
  submitContent?: ReactNode;
  error?: Error | null;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex gap-2.5 p-5 bg-white">
      <Button
        size="default"
        variant="secondary"
        className="gap-1"
        disabled={!onBack}
        onClick={onBack}
      >
        <ArrowLeft className="w-4 h-4 " />
        {t("common.back")}
      </Button>
      <Button size="default" variant="secondary" onClick={onCancel}>
        {t("common.cancel")}
      </Button>
      <ErrorPopover error={error}>
        <Button
          loading={loading}
          size="default"
          variant="default"
          className="gap-1 ml-auto"
          onClick={onNext}
        >
          {submitContent}
          {!submitContent && (
            <>
              {t("common.next")}
              <ArrowRight className="w-4 h-4 " />
            </>
          )}
        </Button>
      </ErrorPopover>
    </div>
  );
}
